<template>
  <dl
    class="card-header wc-panel-header secondary-lighter-7-contrast font-size-sm d-flex justify-content-between"
  >
    <!-- START : Order Placed Info -->
    <div class="wc-panel-header__info">
      <div>
        <dt class="h4 mb-0">{{ $t('orderPlaced') }}</dt>
        <dd class="mb-0">{{ order.endTime | formatDateTime }}</dd>
      </div>
    </div>
    <!-- END : Order Placed Info -->
    <!-- START : Order Total Info -->
    <div class="wc-panel-header__info">
      <div>
        <dt class="h4 mb-0">{{ $t('orderTotal') }}</dt>
        <dd class="mb-0">
          <span v-if="order.total">{{ order.total | currency }}</span>
          <span v-if="!order.total">{{ $t('notAvailableAbbr') }}</span>
        </dd>
      </div>
    </div>
    <!-- END : Order Total Info -->
    <!-- START : Order Status Info -->
    <div class="wc-panel-header__info">
      <div>
        <dt class="h4 mb-0">{{ $t('orderStatus') }}</dt>
        <dd class="mb-0">{{ order.status.label }}</dd>
      </div>
    </div>
    <!-- END : Order Status Info -->
    <!-- START : Order Shipping Address Info -->
    <div v-if="order.shippingAddress" class="wc-panel-header__info">
      <div>
        <dt class="h4 mb-0">{{ $t('shipTo') }}</dt>
        <dd class="mb-0">
          {{ order.shippingAddress.firstName }} {{ order.shippingAddress.lastName }}
          <font-awesome-icon ref="address" icon="circle-question" />
          <WCToolTip target="address" position="bottom">
            <WCCartAddress :address="order.shippingAddress" />
          </WCToolTip>
        </dd>
      </div>
    </div>
    <!-- END : Order Shipping Address Info -->
    <div class="wc-panel-header__action row mr-2" v-if="order.editable">
      <!-- START : Cancel Order Button -->
      <button
        class="btn btn-primary mr-2"
        data-btn="cancelOrder"
        ref="modalTriggerButton"
        @click="$emit('cancelOrder', order)"
      >
        {{ $t('cancelOrder') }}
      </button>
      <!-- END : Cancel Order Button -->
      <!-- START : Edit Order Button -->
      <div v-if="this.$configuration.allowEditOrder">
        <WCEditOrderButton @editOrder="$emit('editOrder', order)" />
      </div>
      <!-- END : Edit Order Button -->
    </div>
    <!-- START : View Receipt Button -->
    <div v-else-if="!order.editable && order.status.stage === 3">
      <button
        @click="$emit('receipt', order)"
        class="btn btn-link underline-link-from-center font-weight-bold mr-0"
        ref="viewReceiptButtonRef"
      >
        {{ $t('viewReceipt') }}
      </button>
    </div>
    <!-- END : View Receipt Button -->
  </dl>
</template>

<script>
import WCCartAddress from '@/components/WCCartAddress/WCCartAddress.vue';
import WCToolTip from '@/components/WCToolTip/WCToolTip.vue';
import WCEditOrderButton from './WCEditOrderButton/WCEditOrderButton.vue';

export default {
  name: 'WCOrderPanelHeader',
  components: { WCToolTip, WCCartAddress, WCEditOrderButton },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  emit: ['cancelOrder', 'receipt', 'editOrder'],
  methods: {
    focus() {
      if (this.$refs.viewReceiptButtonRef) this.$refs.viewReceiptButtonRef.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

/*----- START : Header Panel -----*/
.wc-panel-header {
  background-color: var(--secondary-lighter-7, get-color('secondary', 'lighter-7'));
  flex-wrap: wrap;

  /*----- START : Header Panel Info -----*/
  &__info {
    display: flex;
    flex-direction: column;

    /*----- START : Header Panel Info for Extra Small Screen -----*/
    @include media-breakpoint-only(xs) {
      width: calc(50% - 0.5rem);

      &:nth-of-type(even) {
        align-items: flex-end;
      }
    }
    /*----- END : Header Panel Info for Extra Small Screen -----*/

    /*----- START : Header Panel Info for Small Screen -----*/
    @include media-breakpoint-only(sm) {
      width: calc((100% - 2rem) / 3);
      &:nth-of-type(3n + 2) {
        align-items: center;
      }
      &:nth-of-type(3n) {
        align-items: flex-end;
      }
    }
    /*----- END : Header Panel Info for Small Screen -----*/

    /*----- START : Header Panel Info for Medium device & large device -----*/
    @include media-breakpoint-between(md, lg) {
      width: calc((100% - 3rem) / 4);
      &:nth-of-type(4n + 2),
      &:nth-of-type(4n + 3) {
        align-items: center;
      }
      &:nth-of-type(4n) {
        align-items: flex-end;
      }
    }
    /*----- END : Header Panel Info for Medium device & large device -----*/

    /*----- START : Header Panel Info for Extra Large Screen -----*/
    @include media-breakpoint-up(xl) {
      min-width: calc((100% - 4rem) / 5);
    }
    /*----- END : Header Panel Info for Extra Large Screen -----*/
  }
  /*----- END : Header Panel Info -----*/

  /*----- START : Header Panel Action Buttons -----*/
  &__action {
    text-align: right;

    @include media-breakpoint-down(lg) {
      gap: 1rem;
      width: 100%;
      justify-content: center;
    }
  }
  /*----- END : Header Panel Action Buttons -----*/
}
/*----- END : Header Panel -----*/
</style>
